import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams } from "../router";
import { ChevronLeft, ChevronRight } from "../icons";

import { Button } from "../button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { cn } from "../utils";

export interface TablePagerProps {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
  /**
   * Page size options for the picker. Default `[25, 50, 100, 200]`.
   * Capped at 200 server-side by `paginationQuery`.
   */
  pageSizeOptions?: number[];
  /**
   * Default page size — when the URL `page_size` equals this, the param is
   * removed from the URL (keeps the canonical `/issues` URL clean). Should
   * match the route handler's default (50).
   */
  defaultPageSize?: number;
  /**
   * Name of the URL search param for the page number. Default `"page"`.
   * Override for embedded paginators that share a URL with another list.
   */
  pageParam?: string;
  /** Name of the URL search param for page size. Default `"page_size"`. */
  pageSizeParam?: string;
  /**
   * Optional custom URL builder. When omitted, the pager writes back to the
   * current pathname with `pageParam` / `pageSizeParam`. Use when the pager
   * sits on a different surface than the table it controls.
   */
  buildHref?: (next: { page: number; page_size: number }) => string;
  /** Label shown next to the count. Default `"rows"`. */
  unitLabel?: string;
  className?: string;
}

const DEFAULT_OPTIONS = [25, 50, 100, 200];

/**
 * URL-driven paginator that pairs with the standard paginated response
 * envelope (`{ rows, page, page_size, total, total_pages, has_next, has_prev }`)
 * from `paginatedSchema()`.
 *
 * Renders `1–50 of 423 rows`, prev/next buttons, and a page-size selector.
 * Writes `?page=N` and `?page_size=M` into the URL via Next's router. Empty
 * tables collapse to a single sentence.
 */
export function TablePager({
  page,
  page_size,
  total,
  total_pages,
  has_next,
  has_prev,
  pageSizeOptions = DEFAULT_OPTIONS,
  defaultPageSize = 50,
  pageParam = "page",
  pageSizeParam = "page_size",
  buildHref,
  unitLabel = "rows",
  className,
}: TablePagerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();

  const start = total === 0 ? 0 : (page - 1) * page_size + 1;
  const end = Math.min(total, page * page_size);

  // Include the current page_size in the options if it's a non-standard
  // value (e.g. ?page_size=10 in the URL) so the trigger has something to
  // render instead of an empty label.
  const sizeOptions = pageSizeOptions.includes(page_size)
    ? pageSizeOptions
    : [...pageSizeOptions, page_size].sort((a, b) => a - b);

  function goto(nextPage: number, nextSize: number) {
    if (buildHref) {
      router.replace(buildHref({ page: nextPage, page_size: nextSize }));
      return;
    }
    const next = new URLSearchParams(search.toString());
    if (nextPage <= 1) next.delete(pageParam);
    else next.set(pageParam, String(nextPage));
    if (nextSize === defaultPageSize) next.delete(pageSizeParam);
    else next.set(pageSizeParam, String(nextSize));
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname);
  }

  if (total === 0) {
    return (
      <div
        data-slot="table-pager"
        className={cn(
          "flex items-center justify-between gap-3 px-1 py-2 type-body text-muted-foreground",
          className,
        )}
      >
        <span>0 {unitLabel}</span>
      </div>
    );
  }

  return (
    <div
      data-slot="table-pager"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 px-1 py-2 type-body text-muted-foreground",
        className,
      )}
    >
      <div className="tabular-nums">
        {start.toLocaleString()}–{end.toLocaleString()} of{" "}
        {total.toLocaleString()} {unitLabel}
      </div>
      <div className="flex items-center gap-2">
        <label className="hidden items-center gap-2 sm:flex">
          <span>Per page</span>
          <Select
            value={String(page_size)}
            onValueChange={(value) => goto(1, Number(value))}
          >
            <SelectTrigger size="sm" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sizeOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!has_prev}
            onClick={() => goto(Math.max(1, page - 1), page_size)}
            aria-label="Previous page"
          >
            <ChevronLeft className="size-4" aria-hidden />
          </Button>
          <span className="px-2 tabular-nums">
            Page {page} of {Math.max(1, total_pages)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!has_next}
            onClick={() => goto(page + 1, page_size)}
            aria-label="Next page"
          >
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
